// @flow
import React, { Fragment, useContext, useState } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import { COLORS } from 'Components/utils/color/colors';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { getLinkProps, getLinkTagName } from 'Components/utils/markup/markup';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { TeaserCard } from 'Components/frameworks/teaser-card/TeaserCard.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';

export type ProductFeatureCardProps = {
  banner?: {
    color?: Colors,
    iconName?: IconName,
    label: string,
  },
  className?: string,
  counters?: Array<{
    color?: Colors,
    count: number,
    icon?: IconName,
    label: string,
  }>,
  imageSrc: string,
  isHidden?: boolean,
  key?: string,
  link?: {
    onClick?: () => void,
    target?: string,
    url?: string,
  },
  seeMoreLabel?: string,
  title: string,
  titleColor?: Colors,
  trackingData?: TrackingData,
  width?: number,
};

export function ProductFeatureCard({
  banner = {},
  className,
  counters = [],
  imageSrc,
  isHidden,
  link,
  seeMoreLabel,
  title,
  titleColor,
  trackingData,
  width,
}: ProductFeatureCardProps) {
  const [isHovering, setHovering] = useState(false);
  const { isDesktop } = useContext(ResponsiveContext);

  const media = [{ src: imageSrc }];

  const tagName = getLinkTagName(link);
  const linkProps = getLinkProps(link);

  return (
    <TrackClick
      {...linkProps}
      aria-hidden={isHidden}
      as={tagName}
      className={classnames(className, 'reset-button block relative text-initial w-full')}
      onMouseEnter={isDesktop ? () => setHovering(true) : null}
      onMouseLeave={isDesktop ? () => setHovering(false) : null}
      style={{ width }}
      tabIndex={isHidden ? -1 : 0}
      trackingData={trackingData}
    >
      {!!banner.label && (
        <div
          className={classnames(
            `bg-${banner.color ||
              COLORS.BLUE} absolute flex font-brand items-center justify-center p-3 round-t-tiny text-base sm:text-sm lg:text-base text-center text-white w-full z-1 transition-transform`,
            { 'scale-095': isHovering },
          )}
        >
          <Icon className="rtl:mr-2" name={banner.iconName} width="0.625rem" />
          {banner.label}
        </div>
      )}
      <TeaserCard isActive={isHovering} media={media}>
        <Title className="my-4" color={titleColor} hasEllipsis theme="title-sans-3">
          {title}
        </Title>
        {!isEmpty(counters) && (
          <ul className="reset-list my-4" style={{ height: '2.5rem' }}>
            {counters.map(
              ({ color = COLORS.BLUE, icon, label, count }) =>
                count ? (
                  <li
                    className={`flex font-brand justify-between my-1 overflow-hidden text-base text-${color}`}
                    key={label}
                  >
                    {!!label &&
                      !!count && (
                        <Fragment>
                          <span className="inline-flex items-center max-w-4/5">
                            <Icon className="flex-shrink-0 rtl:mr-2" name={icon} width="1em" />
                            <span className="truncate">{label}</span>
                          </span>
                          {count}
                        </Fragment>
                      )}
                  </li>
                ) : null,
            )}
          </ul>
        )}
        {!!seeMoreLabel && (
          <p className="reset-paragraph my-4 text-center text-sm underline">{seeMoreLabel}</p>
        )}
      </TeaserCard>
    </TrackClick>
  );
}
